<template>
	<div v-if="pageContent" class="container generic" :class="`container--${pageContent?.slug?.current || 'generic'}`">
		<ul
			class="generic__subnav"
			v-if="pageContent?.subNavList?.subNavigationReference?.length"
		>
			<li
				v-for="link in pageContent.subNavList.subNavigationReference"
				:key="link?._id || link?.slug?.current"
				class="subnav__link"
			>
				<nuxtLink
					v-if="link?.slug?.current"
					:to="localePath(`/${link.slug.current}`)"
				>
					{{ locale === 'en' ? link.text?.en : link.text?.jp }}
				</nuxtLink>
			</li>
		</ul>

		<header class="generic__header row" :class="pageContent.isReversed === true ? 'reversed' : ''">
			<div class="generic__intro col-xs-12 col-md-6">
				<h1 class="section__heading generic__heading" v-if="contentHeading">
					{{ contentHeading }}
				</h1>

				<h2 class="generic__subheading" v-if="contentSubheading">
					{{ contentSubheading }}
				</h2>

				<div class="generic__text" v-if="contentText">
					<PortableText :value="contentText" />

					<nuxtLink
						v-if="pageContent?.button?.slug?.current"
						:to="`${locale === 'en' ? '/en' : ''}/${pageContent.button.slug.current}`"
						class="generic__button"
					>
						{{ locale === 'en' ? pageContent.button.text?.en : pageContent.button.text?.jp }}
					</nuxtLink>
				</div>
			</div>

			<div class="col-xs-12 col-md-6">
				<img
					v-if="pageContent?.hero?.asset?.url"
					:src="$urlFor(pageContent.hero.asset.url, 1200)"
					:alt="contentHeroAlt || ''"
					class="generic__hero"
					:height="pageContent.hero.asset.metadata?.dimensions?.height"
					:width="pageContent.hero.asset.metadata?.dimensions?.width"
				/>
			</div>
		</header>

		<div class="generic__full-width" v-if="fullWidthText">
			<h3 class="full-width__heading" v-if="fullWidthHeading">
				{{ fullWidthHeading }}
			</h3>

			<h4 class="full-width__subheading" v-if="fullWidthSubheading">
				{{ fullWidthSubheading }}
			</h4>

			<PortableText :value="fullWidthText" />
		</div>

		<div v-if="thePage === 'company-info'">
			<tokyo-dex-map />
		</div>

		<div v-if="pageContent?.singleImage?.image?.asset?.url">
			<img
				:src="$urlFor(pageContent.singleImage.image.asset.url, 2000)"
				:alt="fullWidthImageAlt || ''"
				class="generic__full-image"
				:height="pageContent.singleImage.image.asset.metadata?.dimensions?.height"
				:width="pageContent.singleImage.image.asset.metadata?.dimensions?.width"
			/>
		</div>

		<div v-if="pageContent?.images?.length" class="generic__gallery">
			<image-gallery :content="pageContent.images" :columns="pageContent.columnsPerRow" />
		</div>

		<div v-if="pageContent?.videos?.length" class="generic__videos">
			<videoEmbed
				v-for="video in pageContent.videos"
				:key="video._key || video.videoId"
				:content="video"
			/>
		</div>

		<cta />

		<div v-if="pageContent?.projectsReference?.projects?.length" class="generic__featured">
			<featured-projects :content="pageContent.projectsReference" />
		</div>
	</div>
</template>


<script setup>
import { groq } from "@nuxtjs/sanity/runtime/groq";
import { useRoute, useHead } from "nuxt/app";
import { computed } from "vue";
import { PortableText } from "@portabletext/vue";
import { useI18n, useLocalePath } from "#imports";

const route = useRoute();
const { locale } = useI18n();
const localePath = useLocalePath();
const { $getSafeLocalizedField } = useNuxtApp();

const thePage = computed(() => {
	const p = route.params.generic
	return Array.isArray(p) ? p[0] : String(p || '')
})

const query = groq`
  *[_type == "generic" && slug.current == $slug][0]{
    title,
    slug,
    metaTitle,
    metaDescription,
    subNavList->{
      subNavigationReference[]->{
        slug,
        href,
        text
      }
    },
    hero{
      asset->{
        metadata{ dimensions{ height, width } },
        url
      },
      alt
    },
    heroAlt,
    isReversed,
    introSubheading,
    intro,
    textHeading,
    button->{
      slug,
      text,
      href
    },
    textSubheading,
    textFullWidth,
    singleVideo{
      videoType, video, videoId, title, isLooping, isAutoplay, hasControls
    },
    videos[]{ ... },
    singleImage{
      alt,
      image{
        asset->{
          metadata{ dimensions{ height, width } },
          url
        }
      }
    },
    columnsPerRow,
    images[]{
      alt,
      image{
        asset->{
          metadata{ dimensions{ height, width } },
          url
        }
      }
    },
    projectsReference{
      title,
      isDarkMode,
      projects[]->{
        title,
        slug,
        projectType,
        projectHero{
          asset->{
            metadata{ dimensions{ height, width } },
            url
          }
        },
        projectHeroAlt
      },
      linkType,
      projectsLinkText
    }
  }
`;

const sanity = useSanity();
const { data, error } = await useAsyncData(
	`page:generic:${route.fullPath}`,
	() => sanity.fetch(query, { slug: thePage.value })
)

const pageContent = computed(() => data.value ?? null)

if (error.value || !pageContent.value) {
	throw createError({ statusCode: 404, statusMessage: "Page not found" })
}


const htmlLang = computed(() => (locale.value === "en" ? "en-US" : "ja-JP"));

// const contentHeading = computed(() =>
// 	$getSafeLocalizedField(pageContent.value?.title, locale.value, "")
// );

const contentHeading = computed(() =>
	locale.value === 'en'
		? pageContent.value?.title?.en
		: pageContent.value?.title?.jp
)

const contentSubheading = computed(() =>
	$getSafeLocalizedField(pageContent.value?.introSubheading, locale.value, "")
);

// const contentText = computed(() =>
// 	$getSafeLocalizedField(pageContent.value?.intro, locale.value, null)
// );

const contentText = computed(() =>
	locale.value === 'en'
		? pageContent.value?.intro?.en
		: pageContent.value?.intro?.jp
)


const contentHeroAlt = computed(() =>
	$getSafeLocalizedField(pageContent.value?.heroAlt, locale.value, "")
);

const fullWidthHeading = computed(() =>
	$getSafeLocalizedField(pageContent.value?.textHeading, locale.value, "")
);

const fullWidthSubheading = computed(() =>
	$getSafeLocalizedField(pageContent.value?.textSubheading, locale.value, "")
);

const fullWidthText = computed(() =>
	$getSafeLocalizedField(pageContent.value?.textFullWidth, locale.value, null)
);

const fullWidthImageAlt = computed(() =>
	$getSafeLocalizedField(pageContent.value?.singleImage?.alt, locale.value, "")
);

const processedTitle = computed(() => {
	const raw = pageContent.value?.metaTitle;
	return raw
		? $getSafeLocalizedField(raw, locale.value, "")
		: `TokyoDex - ${contentHeading.value || ""}`;
});

const fallbackDescription =
	"Since 2012, TokyoDex has been a leader in transforming spaces through innovative art solutions.";

const processedDescription = computed(() => {
	const raw = pageContent.value?.metaDescription;
	return $getSafeLocalizedField(raw, locale.value, fallbackDescription);
});

useHead(() => ({
	htmlAttrs: { lang: htmlLang.value },
	title: processedTitle.value,
	meta: [{ name: "description", content: processedDescription.value }],
}));

</script>

<style lang="scss" scoped>
@use '@/assets/sass/variables.scss' as v;
.container {
	&--error {
		margin: 8.5rem 0 0 0;
	}
}
.generic__subnav {
	margin: 7rem auto 1rem auto;
	padding: 0;
	position: relative;
	@include v.respond-to('small') {
		margin: 7rem auto 2rem auto;
	}
	li {
		display: inline-block;
		margin: 0 0.5rem 0 0;
		padding: 0;
		@include v.respond-to('small') {
			margin: 0 1rem 0 0;
		}
		a {
			@include v.button;
			margin: 0 0 0.5rem 0;
			padding: 0.5rem 0.75rem;
			@include v.respond-to('small') {
				margin: 1rem 0 0 0;
				padding: 0.5rem 1rem;
			}
			&.router-link-active {
				background-color: v.$blueGrey;
				color: v.$canvas;
				cursor: text;
				opacity: 0.75;
			}
		}
	}
}
.generic__header {
	margin: 0 0 1rem 0;
	@include v.respond-to('small') {
		margin: 0 0 3rem 0;
	}
}
.generic__heading {
	font-size: v.$font-size-xl;
	line-height: v.$font-size-xl;
	margin: 0 0 1rem 0;
	padding: 0;
	@include v.respond-to('small') {
		font-size: 4rem;
		line-height: 4rem;
	}
}
.generic__subheading {
	font-size: v.$font-size-lg;
	line-height: 2rem;
	margin: 0 3rem 0 0;
}
.generic__hero {
	display: block;
	height: auto;
}
.generic__text {
	margin: 0;
	@include v.respond-to('small') {
		margin: 0 3rem 0 0;
	}
	p {
		margin: 0;
	}
	ul {
		padding: 0;
		li {
			list-style: none;
		}
	}
	strong {
		margin: 1rem 0 0 0;
	}
}
.generic__full-image {
	height: auto;
	max-height: 90vh;
	object-fit: cover;
	width: 100%;
	margin: 0 0 1rem 0;
	@include v.respond-to('small') {
		margin: 0 0 3rem 0;
	}
}
.generic__button {
	@include v.button;
	margin: 1rem 0 3rem 0;
	@include v.respond-to('small') {
		margin: 1rem 0;
	}
}
.reversed {
	flex-direction: row-reverse;
	.generic__intro {
		.generic__heading {
			margin: 0;
			@include v.respond-to('medium') {
				margin: 0 0 0 3rem;
			}
		}
		.generic__subheading {
			margin: 1rem 0;
			@include v.respond-to('medium') {
				margin: 1rem 0 1rem 3rem;
			}
		}
		.generic__text {
			margin: 0;
			@include v.respond-to('medium') {
				margin: 0 0 0 3rem;
			}
		}
	}
}
.generic__full-width {
	margin: 1rem 0;
	@include v.respond-to('small') {
		margin: 3rem 0;
	}
}
.full-width__heading {
	font-size: v.$font-size-xl;
	line-height: v.$font-size-xl;
	margin: 1rem 0 0 0;
	@include v.respond-to('small') {
		margin: 3rem 0 0 0;
	}
}

.generic__gallery {
	.gallery {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		.gallery__image {
			height: auto;
			object-fit: none;
			margin: 0 0 0.5rem 0;
			width: auto;
			img {
				height: auto;
				object-fit: none;
				object-position: left;
			}
		}
	}
}

.generic__featured {
	margin-top: 3rem;
	.featured-projects {
		padding: 0 0 0 1rem;
		@include v.respond-to('small') {
			padding: 0 0 0 3rem;
		}
	}
}
</style>
